import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiPost } from '../../api/axios';

export default function Dashboard() {
  const [moneySummary, setMoneySummary] = useState(null);
  const [habitsToday, setHabitsToday] = useState([]);
  const [habitStats, setHabitStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const today = new Date();
      const [moneyRes, habitsRes, statsRes] = await Promise.all([
        apiPost({
          action: 'get_query',
          query: 'money_summary',
          params: { year: today.getFullYear(), month: today.getMonth() + 1 },
        }),
        apiPost({ action: 'habit_today' }),
        apiPost({
          action: 'habit_monthly_stats',
          params: { year: today.getFullYear(), month: today.getMonth() + 1 },
        }),
      ]);
      if (moneyRes.flag === 'success') setMoneySummary(moneyRes.data);
      if (habitsRes.flag === 'success') setHabitsToday(habitsRes.data);
      if (statsRes.flag === 'success') setHabitStats(statsRes.data);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const todayDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  const balance = moneySummary ? moneySummary.balance : 0;
  const totalIncome = moneySummary ? moneySummary.totalIncome : 0;
  const totalExpense = moneySummary ? moneySummary.totalExpense : 0;
  const recentTransactions = moneySummary ? (moneySummary.transactions || []).slice(0, 3) : [];

  const completedToday = habitsToday.filter(h => h.completed).length;
  const totalHabits = habitsToday.length;
  const habitSuccessRate = habitStats ? habitStats.successRate : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Welcome Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              {getGreeting()}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{todayDate}</p>
          </div>
          <button
            onClick={fetchData}
            className="px-4 py-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            Refresh
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-300">
            <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="flex-1">{error}</span>
            <button onClick={() => setError('')} className="text-sm hover:text-red-800 dark:hover:text-red-200">✕</button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Balance"
            value={`₹${balance.toFixed(2)}`}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            color="blue"
          />
          <StatCard
            title="Income (Month)"
            value={`₹${totalIncome.toFixed(2)}`}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            }
            color="emerald"
          />
          <StatCard
            title="Expense (Month)"
            value={`₹${totalExpense.toFixed(2)}`}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
              </svg>
            }
            color="red"
          />
          <StatCard
            title="Habits Completed"
            value={`${completedToday}/${totalHabits}`}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            color="purple"
          />
        </div>

        {/* Two-column main content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Transactions */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Recent Transactions</h3>
              <Link
                to="/money/transactions"
                className="text-sm text-brand-600 dark:text-brand-400 hover:underline"
              >
                View all
              </Link>
            </div>
            {recentTransactions.length === 0 ? (
              <p className="text-sm text-slate-400">No transactions this month.</p>
            ) : (
              <div className="space-y-3">
                {recentTransactions.map(txn => (
                  <div key={txn.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        txn.type === 'income'
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      }`}>
                        {txn.type === 'income' ? '+' : '-'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{txn.category_name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{txn.txn_date}</p>
                      </div>
                    </div>
                    <div className={`text-sm font-semibold ${
                      txn.type === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {txn.type === 'income' ? '+' : '-'} ₹{parseFloat(txn.amount).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Today's Habits */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Today's Habits</h3>
              <Link
                to="/daily/today"
                className="text-sm text-brand-600 dark:text-brand-400 hover:underline"
              >
                View all
              </Link>
            </div>
            {totalHabits === 0 ? (
              <p className="text-sm text-slate-400">No habits created yet.</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {completedToday} of {totalHabits} completed
                  </span>
                  <span className="text-sm font-medium text-brand-600 dark:text-brand-400">
                    {(habitSuccessRate * 100).toFixed(0)}% monthly
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full mb-4">
                  <div
                    className="h-2 bg-brand-500 rounded-full transition-all duration-500"
                    style={{ width: `${completedToday > 0 ? (completedToday / totalHabits) * 100 : 0}%` }}
                  />
                </div>
                <div className="space-y-2">
                  {habitsToday.slice(0, 4).map(habit => (
                    <div key={habit.id} className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${habit.completed ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                      <span className={`text-sm truncate ${habit.completed ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-300'}`}>
                        {habit.name}
                      </span>
                    </div>
                  ))}
                  {totalHabits > 4 && (
                    <p className="text-xs text-slate-400">+{totalHabits - 4} more</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Quick Links</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <QuickLinkCard
              to="/money/dashboard"
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              title="Money"
              color="blue"
            />
            <QuickLinkCard
              to="/money/reports"
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              }
              title="Reports"
              color="emerald"
            />
            <QuickLinkCard
              to="/daily/today"
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              }
              title="Habits"
              color="purple"
            />
            <QuickLinkCard
              to="/profile"
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              }
              title="Profile"
              color="orange"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Reusable Sub-components ----------

function StatCard({ title, value, icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400',
  }[color] || 'bg-slate-50 text-slate-600 dark:bg-slate-900/20 dark:text-slate-400';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-slate-200 dark:border-slate-700 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClasses}`}>
          {icon}
        </div>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">{title}</p>
          <p className="text-lg font-bold text-slate-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function QuickLinkCard({ to, icon, title, color }) {
  const bgClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/40',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/40',
  }[color] || 'bg-slate-50 dark:bg-slate-900/20 text-slate-600 dark:text-slate-400';

  return (
    <Link
      to={to}
      className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-colors ${bgClasses}`}
    >
      {icon}
      <span className="text-sm font-medium">{title}</span>
    </Link>
  );
}